"use client"

import * as React from "react"

import {
  ClientPortal,
  Slot,
  type Align,
  type Side,
  mergeRefs,
  useAnchoredPosition,
  useControllableState,
} from "@/components/ui/internal"
import { tooltipClassName } from "@/components/ui/uiClasses"
import { cn } from "@/lib/utils"

const TooltipProviderContext = React.createContext<{ delayDuration: number }>({
  delayDuration: 0,
})

type TooltipContextValue = {
  open: boolean
  setOpen: (open: boolean) => void
  openWithDelay: () => void
  close: () => void
  triggerRef: React.RefObject<HTMLElement | null>
  contentId: string
}

const TooltipContext = React.createContext<TooltipContextValue | null>(null)

function useTooltipContext(component: string) {
  const context = React.useContext(TooltipContext)
  if (!context) {
    throw new Error(`${component} must be used within Tooltip`)
  }
  return context
}

function TooltipProvider({
  delayDuration = 0,
  children,
}: {
  delayDuration?: number
  children: React.ReactNode
}) {
  return (
    <TooltipProviderContext.Provider value={{ delayDuration }}>
      {children}
    </TooltipProviderContext.Provider>
  )
}

function Tooltip({
  open,
  defaultOpen = false,
  onOpenChange,
  children,
}: {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}) {
  const { delayDuration } = React.useContext(TooltipProviderContext)
  const [isOpen, setIsOpen] = useControllableState({
    prop: open,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  })
  const triggerRef = React.useRef<HTMLElement | null>(null)
  const timeoutRef = React.useRef<number | null>(null)
  const contentId = React.useId()

  const clearDelay = React.useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  React.useEffect(() => clearDelay, [clearDelay])

  const close = React.useCallback(() => {
    clearDelay()
    setIsOpen(false)
  }, [clearDelay, setIsOpen])

  const openWithDelay = React.useCallback(() => {
    clearDelay()
    timeoutRef.current = window.setTimeout(() => {
      setIsOpen(true)
    }, delayDuration)
  }, [clearDelay, delayDuration, setIsOpen])

  return (
    <TooltipContext.Provider
      value={{
        open: Boolean(isOpen),
        setOpen: (nextOpen) => {
          clearDelay()
          setIsOpen(nextOpen)
        },
        openWithDelay,
        close,
        triggerRef,
        contentId,
      }}
    >
      {children}
    </TooltipContext.Provider>
  )
}

const TooltipTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & {
    asChild?: boolean
    children?: React.ReactNode
    type?: "button" | "submit" | "reset"
  }
>(function TooltipTrigger(
  {
    asChild = false,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    onClick,
    children,
    ...props
  },
  forwardedRef
) {
  const tooltip = useTooltipContext("TooltipTrigger")

  if (asChild) {
    return (
      <Slot
        ref={mergeRefs(forwardedRef, tooltip.triggerRef)}
        data-slot="tooltip-trigger"
        aria-describedby={tooltip.open ? tooltip.contentId : undefined}
        onMouseEnter={(event) => {
          onMouseEnter?.(event)
          tooltip.openWithDelay()
        }}
        onMouseLeave={(event) => {
          onMouseLeave?.(event)
          tooltip.close()
        }}
        onFocus={(event) => {
          onFocus?.(event)
          tooltip.openWithDelay()
        }}
        onBlur={(event) => {
          onBlur?.(event)
          tooltip.close()
        }}
        onClick={(event) => {
          onClick?.(event)
          tooltip.close()
        }}
        {...props}
      >
        {children as React.ReactElement}
      </Slot>
    )
  }

  return (
    <button
      ref={mergeRefs(
        forwardedRef as React.Ref<HTMLButtonElement>,
        tooltip.triggerRef as React.Ref<HTMLButtonElement>
      )}
      data-slot="tooltip-trigger"
      aria-describedby={tooltip.open ? tooltip.contentId : undefined}
      type={props.type ?? "button"}
      onMouseEnter={(event) => {
        onMouseEnter?.(event)
        tooltip.openWithDelay()
      }}
      onMouseLeave={(event) => {
        onMouseLeave?.(event)
        tooltip.close()
      }}
      onFocus={(event) => {
        onFocus?.(event)
        tooltip.openWithDelay()
      }}
      onBlur={(event) => {
        onBlur?.(event)
        tooltip.close()
      }}
      onClick={(event) => {
        onClick?.(event)
        tooltip.close()
      }}
      {...props}
    >
      {children}
    </button>
  )
})

const TooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    side?: Side
    align?: Align
    sideOffset?: number
  }
>(function TooltipContent(
  { className, side = "top", align = "center", sideOffset = 4, ...props },
  forwardedRef
) {
  const tooltip = useTooltipContext("TooltipContent")
  const contentRef = React.useRef<HTMLDivElement | null>(null)
  const { side: resolvedSide, style } = useAnchoredPosition({
    open: tooltip.open,
    anchorRef: tooltip.triggerRef,
    contentRef,
    side,
    align,
    sideOffset,
  })

  if (!tooltip.open) {
    return null
  }

  return (
    <ClientPortal>
      <div
        ref={mergeRefs(forwardedRef, contentRef)}
        id={tooltip.contentId}
        data-slot="tooltip-content"
        data-side={resolvedSide}
        role="tooltip"
        className={cn(tooltipClassName, className)}
        style={style}
        {...props}
      />
    </ClientPortal>
  )
})

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
